import { ChevronDown } from "lucide-react";
import { DetalheDaLinha } from "./DetalheDaLinha";
import { cn } from "@/lib/cn";
import { AvatarDaOrganizacao } from "@/components/layout/AvatarDaOrganizacao";
import { papelDe, type Grupo, type OrgDoPainel } from "./painel";

type Sinal = { rotulo: string; valor: number | null | undefined; ruim?: boolean };

/** Os dois números que mais dizem de cada tipo: na clínica, hoje e atrasados; no laboratório,
 *  aguardando aceite e atrasados. */
function sinais(o: OrgDoPainel): Sinal[] {
  const l = o.laboratorio;
  const c = o.clinica;
  if (l) return [{ rotulo: "aceite", valor: l.aguardandoAceite }, { rotulo: "atrasados", valor: l.atrasados, ruim: true }];
  if (c) return [{ rotulo: "hoje", valor: c.consultasHoje }, { rotulo: "atrasados", valor: c.pedidosAtrasados, ruim: true }];
  return [];
}

type Props = {
  grupos: Grupo[];
  ativaId: string | undefined;
  naoLidas: Record<string, number>;
  aberta: string | null;
  trocando: string | null;
  onAlternar: (o: OrgDoPainel) => void;
  onTrocar: (o: OrgDoPainel) => void;
  onRecolher: (o: OrgDoPainel) => void;
};

/** No celular a tabela vira linhas compactas; tocar expande o mês ali mesmo. */
export function ListaNoCelular({ grupos, ativaId, naoLidas, aberta, trocando, onAlternar, onTrocar, onRecolher }: Props) {
  return (
    <div className="space-y-4">
      {grupos.map((g) => (
        <section key={g.chave}>
          <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
            {g.titulo} · {g.orgs.length}
          </p>
          <ul className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white dark:divide-border/50 dark:border-border dark:bg-card">
            {g.orgs.map((o) => {
              const n = naoLidas[o.id] ?? 0;
              return (
                <li key={o.id} id={`org-${o.id}`}>
                  <button type="button" aria-expanded={aberta === o.id} onClick={() => onAlternar(o)} className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-zinc-50 dark:active:bg-muted/40">
                    <AvatarDaOrganizacao id={o.id} nome={o.nome} logo={o.logoUrl} pessoal={o.pessoal} className="h-10 w-10" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{o.nome}</span>
                        {n > 0 && <span className="shrink-0 rounded-full bg-zinc-100 px-1.5 text-[10px] font-bold text-zinc-600 dark:bg-muted">{n > 99 ? "99+" : n}</span>}
                      </span>
                      <span className="block truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
                        {o.id === ativaId ? <span className="font-semibold text-emerald-600">Você está aqui · </span> : null}
                        {papelDe(o)}
                      </span>
                    </span>
                    {sinais(o).map((s) => (
                      <span key={s.rotulo} className="w-12 shrink-0 text-center">
                        <span className={cn("block text-base font-bold tabular-nums leading-none", s.valor == null ? "text-zinc-300" : s.ruim && s.valor > 0 ? "text-red-600" : s.valor ? "text-zinc-900 dark:text-foreground" : "text-zinc-400")}>
                          {s.valor ?? "—"}
                        </span>
                        <span className="text-[10px] text-zinc-500 dark:text-muted-foreground">{s.rotulo}</span>
                      </span>
                    ))}
                    <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-300 transition-transform", aberta === o.id ? "rotate-0" : "-rotate-90")} />
                  </button>
                  {aberta === o.id && <DetalheDaLinha org={o} ativa={o.id === ativaId} trocando={trocando === o.id} onTrocar={() => onTrocar(o)} onRecolher={() => onRecolher(o)} />}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
